import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import {
  Tag, Plus, Pause, Play, Trash, PencilSimple, CheckCircle, Clock, Calendar, Copy, ChartBar, X, CircleNotch,
} from "@phosphor-icons/react";

const PLAN_IDS = ["starter", "pro", "scale"];

const STATUS_STYLES = {
  active: { label: "Active", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  scheduled: { label: "Scheduled", color: "bg-blue-50 text-blue-700 border-blue-200" },
  paused: { label: "Paused", color: "bg-zinc-100 text-zinc-600 border-zinc-200" },
  expired: { label: "Expired", color: "bg-amber-50 text-amber-700 border-amber-200" },
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (d) => new Date(Date.now() + d * 86400000).toISOString().slice(0, 10);

const emptyForm = () => ({
  code: "",
  description: "",
  discount_pct: 25,
  start_date: todayISO(),
  end_date: addDaysISO(30),
  applies_to_plan_ids: ["pro", "scale"],
  is_active: true,
});

export default function Promotions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  // Performance drawer state. `perf` is the snapshot loaded for `viewingPromo`.
  const [viewingPromo, setViewingPromo] = useState(null);
  const [perf, setPerf] = useState(null);
  const [perfLoading, setPerfLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/promotions");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load promotions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      code: row.code,
      description: row.description || "",
      discount_pct: row.discount_pct,
      start_date: row.start_date,
      end_date: row.end_date,
      applies_to_plan_ids: row.applies_to_plan_ids || [],
      is_active: !!row.is_active,
    });
    setDialogOpen(true);
  };

  const togglePlan = (pid) => {
    setForm((f) => ({
      ...f,
      applies_to_plan_ids: f.applies_to_plan_ids.includes(pid)
        ? f.applies_to_plan_ids.filter((p) => p !== pid)
        : [...f.applies_to_plan_ids, pid],
    }));
  };

  const save = async () => {
    if (!form.code.trim()) return toast.error("Code is required");
    if (form.discount_pct < 1 || form.discount_pct > 100) return toast.error("Discount must be 1-100");
    if (form.applies_to_plan_ids.length === 0) return toast.error("Select at least one plan");
    if (form.end_date < form.start_date) return toast.error("End date can't be before start date");
    setSaving(true);
    try {
      if (editing) {
        // The code is immutable after creation — easier auditing + no link-rot.
        const { code, ...patch } = form;
        await api.patch(`/admin/promotions/${editing.id}`, patch);
        toast.success("Promotion updated");
      } else {
        await api.post("/admin/promotions", form);
        toast.success("Promotion created");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete promo ${row.code}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/promotions/${row.id}`);
      toast.success("Promotion deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  const togglePause = async (row) => {
    try {
      await api.patch(`/admin/promotions/${row.id}`, { is_active: !row.is_active });
      toast.success(row.is_active ? "Promotion paused" : "Promotion resumed");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed");
    }
  };

  const openPerformance = async (row) => {
    setViewingPromo(row);
    setPerf(null);
    setPerfLoading(true);
    try {
      const { data } = await api.get(`/admin/promotions/${row.id}/performance`);
      setPerf(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load performance");
      setViewingPromo(null);
    } finally {
      setPerfLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl" data-testid="promotions-page">
      <Toaster richColors position="top-right" />
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1">
            Platform offers
          </div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans" }}>
            Promotions
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Run discounts on Pro and Scale subscription plans. After a promo ends, prices return to original.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="new-promo-btn" className="bg-zinc-900 hover:bg-zinc-800 text-white">
          <Plus size={14} className="mr-1.5" weight="bold" />
          New promotion
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading promotions…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-zinc-200 rounded-md p-10 text-center" data-testid="promotions-empty">
          <div className="w-12 h-12 mx-auto rounded-md bg-amber-50 text-amber-700 flex items-center justify-center mb-3">
            <Tag size={22} weight="bold" />
          </div>
          <div className="font-medium text-zinc-700">No promotions yet</div>
          <div className="text-sm text-zinc-500 mt-1 mb-4">
            Create your first promo to run a discount on Pro or Scale plans.
          </div>
          <Button onClick={openCreate} className="bg-zinc-900 hover:bg-zinc-800 text-white">
            <Plus size={14} className="mr-1.5" weight="bold" /> New promotion
          </Button>
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-md overflow-hidden" data-testid="promotions-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Code</th>
                <th className="text-left px-4 py-2 font-semibold">Discount</th>
                <th className="text-left px-4 py-2 font-semibold">Window</th>
                <th className="text-left px-4 py-2 font-semibold">Plans</th>
                <th className="text-left px-4 py-2 font-semibold">Redemptions</th>
                <th className="text-left px-4 py-2 font-semibold">Status</th>
                <th className="text-right px-4 py-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                const stat = STATUS_STYLES[row.status] || STATUS_STYLES.expired;
                return (
                  <tr key={row.id} className="border-t border-zinc-100" data-testid={`promo-row-${row.code}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <code className="font-mono font-semibold text-zinc-900 bg-zinc-50 border border-zinc-200 rounded px-1.5 py-0.5">
                          {row.code}
                        </code>
                        <button
                          type="button"
                          onClick={() => { navigator.clipboard.writeText(row.code); toast.success("Code copied"); }}
                          className="text-zinc-400 hover:text-zinc-700"
                          title="Copy code"
                          data-testid={`promo-copy-${row.code}`}
                        >
                          <Copy size={13} />
                        </button>
                      </div>
                      {row.description && (
                        <div className="text-xs text-zinc-500 mt-1 max-w-[260px] truncate">{row.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium text-zinc-900">{row.discount_pct}% off</td>
                    <td className="px-4 py-3 text-zinc-600 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-xs">
                        <Calendar size={11} /> {row.start_date}
                      </div>
                      <div className="text-xs text-zinc-400 ml-3.5">→ {row.end_date}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(row.applies_to_plan_ids || []).map((p) => (
                          <span key={p} className="text-[10px] uppercase tracking-wider font-semibold bg-zinc-100 text-zinc-700 rounded px-1.5 py-0.5">
                            {p}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-zinc-700">{row.redemption_count || 0}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-semibold tracking-wider border rounded-full px-2 py-0.5 ${stat.color}`}>
                        {row.status === "active" && <CheckCircle size={10} weight="fill" />}
                        {row.status === "scheduled" && <Clock size={10} weight="fill" />}
                        {stat.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => openPerformance(row)}
                        title="View performance"
                        data-testid={`promo-perf-${row.code}`}
                        className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-zinc-100 text-zinc-600"
                      >
                        <ChartBar size={13} weight="bold" />
                      </button>
                      <button
                        type="button"
                        onClick={() => togglePause(row)}
                        title={row.is_active ? "Pause" : "Resume"}
                        data-testid={`promo-pause-${row.code}`}
                        className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-zinc-100 text-zinc-600 ml-1"
                      >
                        {row.is_active ? <Pause size={13} weight="bold" /> : <Play size={13} weight="bold" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => openEdit(row)}
                        title="Edit"
                        data-testid={`promo-edit-${row.code}`}
                        className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-zinc-100 text-zinc-600 ml-1"
                      >
                        <PencilSimple size={13} weight="bold" />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(row)}
                        disabled={(row.redemption_count || 0) > 0}
                        title={(row.redemption_count || 0) > 0 ? "Cannot delete a redeemed promo — pause it instead" : "Delete"}
                        data-testid={`promo-delete-${row.code}`}
                        className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-red-50 text-red-600 ml-1 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Trash size={13} weight="bold" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="promo-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.code}` : "New promotion"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="promo-code-input" className="text-xs uppercase tracking-wider text-zinc-500">Code</Label>
              <Input
                id="promo-code-input"
                value={form.code}
                disabled={!!editing}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="LAUNCH50"
                className="font-mono"
                data-testid="promo-code-input"
              />
              {editing && <p className="text-xs text-zinc-400 mt-1">Code can&apos;t be changed after creation.</p>}
            </div>
            <div>
              <Label htmlFor="promo-desc-input" className="text-xs uppercase tracking-wider text-zinc-500">Description (shown on landing banner)</Label>
              <Input
                id="promo-desc-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="50% off your first month of Pro or Scale!"
                data-testid="promo-desc-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="promo-discount-input" className="text-xs uppercase tracking-wider text-zinc-500">Discount %</Label>
                <Input
                  id="promo-discount-input"
                  type="number" min="1" max="100"
                  value={form.discount_pct}
                  onChange={(e) => setForm({ ...form, discount_pct: parseInt(e.target.value || "0", 10) })}
                  data-testid="promo-discount-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Applies to plans</Label>
                <div className="flex gap-1.5 mt-1.5">
                  {PLAN_IDS.map((pid) => (
                    <button
                      key={pid}
                      type="button"
                      onClick={() => togglePlan(pid)}
                      data-testid={`promo-plan-${pid}`}
                      className={
                        "text-xs font-medium px-2.5 py-1.5 rounded border transition-colors " +
                        (form.applies_to_plan_ids.includes(pid)
                          ? "bg-zinc-900 text-white border-zinc-900"
                          : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400")
                      }
                    >
                      {pid}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="promo-start-input" className="text-xs uppercase tracking-wider text-zinc-500">Start date</Label>
                <Input
                  id="promo-start-input"
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  data-testid="promo-start-input"
                />
              </div>
              <div>
                <Label htmlFor="promo-end-input" className="text-xs uppercase tracking-wider text-zinc-500">End date</Label>
                <Input
                  id="promo-end-input"
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  data-testid="promo-end-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="promo-save-btn">
              {saving ? "Saving…" : (editing ? "Save changes" : "Create promotion")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Performance drawer — slides in from the right */}
      {viewingPromo && (
        <PerformanceDrawer
          promo={viewingPromo}
          perf={perf}
          loading={perfLoading}
          onClose={() => { setViewingPromo(null); setPerf(null); }}
        />
      )}
    </div>
  );
}

/**
 * Per-promo performance drawer. Slides in from the right and shows three KPIs
 * (redemptions, conversion %, total discount given), plus a chronological
 * timeline of each redemption with paid/unpaid status. Lets ops answer:
 * "is this promo bringing in paying customers, or just freeloaders?"
 */
function PerformanceDrawer({ promo, perf, loading, onClose }) {
  const fmtUsd = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="promo-perf-drawer">
      {/* Scrim */}
      <button
        type="button"
        aria-label="Close performance"
        onClick={onClose}
        className="flex-1 bg-zinc-900/40 backdrop-blur-[2px]"
      />
      {/* Panel */}
      <div className="w-full max-w-xl bg-white dark:bg-zinc-900 h-full overflow-y-auto border-l border-zinc-200 dark:border-zinc-800 shadow-2xl">
        <div className="sticky top-0 bg-white dark:bg-zinc-900 border-b border-zinc-100 dark:border-zinc-800 px-6 py-4 flex items-start justify-between gap-3 z-10">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Performance</div>
            <div className="flex items-center gap-2 mt-1">
              <code className="font-mono font-semibold text-zinc-900 dark:text-zinc-100 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded px-1.5 py-0.5">{promo.code}</code>
              <span className="text-sm text-zinc-500">{promo.discount_pct}% off · {(promo.applies_to_plan_ids || []).join(", ")}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="promo-perf-close"
            className="w-8 h-8 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center justify-center text-zinc-600"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {loading || !perf ? (
          <div className="p-10 flex items-center justify-center text-zinc-500" data-testid="promo-perf-loading">
            <CircleNotch size={20} className="animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* KPIs */}
            <div className="grid grid-cols-3 gap-3" data-testid="promo-perf-kpis">
              <KPI
                label="Redemptions"
                value={perf.redemptions_count}
                sub={`${perf.paid_count} paid`}
              />
              <KPI
                label="Conversion"
                value={perf.conversion_rate_pct == null ? "—" : `${perf.conversion_rate_pct}%`}
                sub={perf.redemptions_count > 0 ? `${perf.paid_count}/${perf.redemptions_count}` : "no signups yet"}
              />
              <KPI
                label="Discount given"
                value={fmtUsd(perf.total_discount_given_usd)}
                sub={`Revenue captured ${fmtUsd(perf.total_revenue_captured_usd)}`}
              />
            </div>

            {/* Timeline */}
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">
                Redemption timeline
              </div>
              {perf.timeline.length === 0 ? (
                <div className="text-sm text-zinc-500 border border-dashed border-zinc-200 rounded p-6 text-center" data-testid="promo-perf-empty">
                  No one has redeemed this code yet.
                </div>
              ) : (
                <ul className="space-y-2" data-testid="promo-perf-timeline">
                  {perf.timeline.map((row) => (
                    <li
                      key={row.invoice_id}
                      className="border border-zinc-100 dark:border-zinc-800 rounded-md p-3 flex items-start gap-3"
                    >
                      <div className={
                        "w-2 h-2 rounded-full mt-2 shrink-0 " +
                        (row.status === "paid" ? "bg-emerald-500" : "bg-amber-400")
                      } />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-medium text-zinc-900 dark:text-zinc-100 truncate">
                            {row.company_name}
                          </div>
                          <span className={
                            "text-[10px] uppercase font-semibold tracking-wider rounded px-1.5 py-0.5 " +
                            (row.status === "paid"
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              : "bg-amber-50 text-amber-800 border border-amber-200")
                          }>
                            {row.status === "paid" ? "Paid" : "Unpaid"}
                          </span>
                        </div>
                        <div className="text-xs text-zinc-500 mt-0.5">
                          {(row.plan_id || "—").toUpperCase()} · Redeemed {fmtDate(row.redeemed_at)}
                          {row.paid_at && <> · Paid {fmtDate(row.paid_at)}</>}
                        </div>
                        <div className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">
                          <span className="font-medium">{fmtUsd(row.discounted_amount)}</span>
                          <span className="text-zinc-400 line-through ml-2">{fmtUsd(row.original_amount)}</span>
                          <span className="ml-2 text-emerald-700">saved {fmtUsd(row.discount_amount)}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ label, value, sub }) {
  return (
    <div className="border border-zinc-100 dark:border-zinc-800 rounded-md p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100 mt-1" style={{ fontFamily: "Work Sans" }}>
        {value}
      </div>
      {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}
